import * as Sentry from '@sentry/vue'
import type { App } from 'vue'
import type { Router } from 'vue-router'
import { useAuthStore } from '@/modules/authentication/stores/auth'

interface SentryConfig {
  app: App
  router?: Router
  dsn: string
  projectName: 'gateway-backend' | 'tenant-backend'
}

export function initSentry({ app, router, dsn, projectName }: SentryConfig) {
  // Check if Sentry should be enabled
  const sentryEnabled = import.meta.env.VITE_SENTRY_ENABLED !== 'false'
  if (!sentryEnabled) {
    console.log('Sentry is disabled in this environment')
    return
  }

  // Use DSN from environment variable if available
  const finalDsn = import.meta.env.VITE_SENTRY_DSN || dsn

  // Determine environment based on the hostname and build mode
  const hostname = window.location.hostname
  const mode = import.meta.env.MODE || 'development'
  const envVar = import.meta.env.VITE_APP_ENVIRONMENT

  let environment = 'development'
  let tracesSampleRate = 1.0
  let replaysSessionSampleRate = 1.0
  let replaysOnErrorSampleRate = 1.0

  // Environment detection - prioritize env variable, then mode, then hostname
  if (envVar) {
    environment = envVar
  } else if (
    mode === 'production' ||
    (hostname.includes('.hsc.health') && !hostname.includes('staging'))
  ) {
    environment = 'production'
  } else if (mode === 'staging' || hostname.includes('staging')) {
    environment = 'staging'
  } else {
    environment = 'development'
  }

  // Set sample rates based on environment
  switch (environment) {
    case 'production':
      tracesSampleRate = 0.1
      replaysSessionSampleRate = 0.1
      replaysOnErrorSampleRate = 1.0
      break
    case 'staging':
      tracesSampleRate = 0.5
      replaysSessionSampleRate = 0.2
      replaysOnErrorSampleRate = 1.0
      break
    default: // development
      tracesSampleRate = 1.0
      replaysSessionSampleRate = 1.0
      replaysOnErrorSampleRate = 1.0
  }

  // Get tenant slug if available (for tenant-backend)
  const tenantSlug = getTenantSlugFromUrl()

  // Get release version from package.json or git commit
  const release = __APP_VERSION__ || 'unknown'

  const integrations: any[] = [
    Sentry.browserTracingIntegration({
      router,
      enableInp: true
    }),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: true,
      maskAllInputs: true,
      networkDetailAllowUrls: ['localhost', '*.hsc.health', 'api.*.hsc.health'],
      networkRequestHeaders: ['X-Request-Id'],
      networkResponseHeaders: ['X-Request-Id']
    })
  ]

  Sentry.init({
    app,
    dsn: finalDsn,
    environment,
    release: `${projectName}@${release}`,
    integrations,

    // Performance Monitoring
    tracesSampleRate,

    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      'localhost',
      /^http:\/\/localhost:\d+/,
      /^https:\/\/[a-zA-Z0-9.-]+\.hsc\.health/,
      /^https:\/\/api\.[a-zA-Z0-9.-]+\.hsc\.health/
    ],

    // Session Replay
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,

    // Additional options
    ignoreErrors: [
      // Ignore common browser errors
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      // Ignore network errors that are expected
      /NetworkError/i,
      /Failed to fetch/i
    ],

    // Set initial tags
    initialScope: {
      tags: {
        'application.type':
          projectName === 'gateway-backend' ? 'gateway-frontend' : 'tenant-frontend',
        'application.name': projectName,
        browser: true,
        ...(tenantSlug && projectName === 'tenant-backend' ? { 'tenant.slug': tenantSlug } : {})
      }
    },

    // Hook to add additional context
    beforeSend(event, hint) {
      // Add additional tags
      if (event.tags) {
        event.tags['page.url'] = window.location.href
        event.tags['page.referrer'] = document.referrer || 'direct'
      }

      // Filter out sensitive data
      if (event.request?.cookies) {
        delete event.request.cookies
      }

      return event
    },

    // Transaction sampling
    tracesSampler: (samplingContext) => {
      // Ignore health check endpoints
      if (samplingContext.name?.includes('/health') || samplingContext.name?.includes('/metrics')) {
        return 0
      }

      // Use default sample rate for other transactions
      return tracesSampleRate
    }
  })

  const { events } = useAuthStore()

  events.addUserLoaded((user) => {
    Sentry.setUser({ id: user.profile.sub })
  })

  events.addUserUnloaded(() => {
    Sentry.setUser(null)
  })
}

function getTenantSlugFromUrl(): string | undefined {
  const hostname = window.location.hostname
  const match = hostname.match(/^([a-zA-Z0-9-]+)\.(staging\.|)hsc\.health$/)
  return match ? match[1] : undefined
}

export const captureException = (error: Error, context?: Record<string, any>) => {
  Sentry.captureException(error, {
    extra: context
  })
}

export const captureMessage = (message: string, level: Sentry.SeverityLevel = 'info') => {
  Sentry.captureMessage(message, level)
}

export const addBreadcrumb = (breadcrumb: Sentry.Breadcrumb) => {
  Sentry.addBreadcrumb(breadcrumb)
}

export const setContext = (name: string, context: Record<string, any>) => {
  Sentry.setContext(name, context)
}
